import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import MangaCard from '@/components/feature/MangaCard';
import { getMangaById, getRelatedMangas, getGenreIdByName } from '@/mocks/manga';

interface ChapterItem {
  n: number;
  title: string;
}

export default function MangaDetailPage() {
  const { mangaId = '' } = useParams();
  const manga = getMangaById(mangaId);

  const [collected, setCollected] = useState(false);
  const [following, setFollowing] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  const [chapterCount, setChapterCount] = useState(12);
  const [ascOrder, setAscOrder] = useState(false);

  const chapters = useMemo<ChapterItem[]>(() => {
    if (!manga) return [];
    const arr: ChapterItem[] = [];
    for (let n = 1; n <= manga.chapters; n += 1) arr.push({ n, title: `第${n}话` });
    return ascOrder ? arr : arr.reverse();
  }, [manga, ascOrder]);

  usePageMeta({
    title: manga ? `${manga.title}漫画在线看 - 免费51漫画` : '漫画详情 - 免费51漫画',
    description: manga
      ? `${manga.title}由${manga.author}创作，类型：${manga.genres.join('、')}，目前${manga.status}，共${manga.chapters}话。免费51漫画在线观看。`
      : '免费51漫画漫画详情页面。',
    path: `/manga/${mangaId}`,
    jsonLd: manga
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'CreativeWorkSeries',
            name: manga.title,
            url: `https://shonenx.github.io/manga/${manga.id}`,
            author: { '@type': 'Person', name: manga.author },
            description: manga.description,
            genre: manga.genres,
            image: manga.cover,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: manga.score,
              bestRating: 10,
              ratingCount: Math.round(Number(manga.views.replace('万', '')) * 13),
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://shonenx.github.io/' },
              { '@type': 'ListItem', position: 2, name: manga.genres[0] || '漫画', item: `https://shonenx.github.io/category/${manga.genres[0]}` },
              { '@type': 'ListItem', position: 3, name: manga.title, item: `https://shonenx.github.io/manga/${manga.id}` },
            ],
          },
        ]
      : undefined,
  });

  if (!manga) {
    return (
      <main className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center">
        <i className="ri-file-unknow-line text-5xl text-foreground-400"></i>
        <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950">未找到该漫画</h1>
        <Link
          to="/"
          className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </main>
    );
  }

  const related = getRelatedMangas(manga);
  const visibleChapters = chapters.slice(0, chapterCount);

  return (
    <main className="w-full bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <nav className="text-sm text-foreground-600" aria-label="面包屑">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
              <li>
                <Link to={`/category/${getGenreIdByName(manga.genres[0])}`} className="hover:text-primary-600">
                  {manga.genres[0]}漫画
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
              <li aria-current="page" className="font-medium text-foreground-900">{manga.title}</li>
            </ol>
          </nav>

          <div className="mt-6 flex flex-col gap-6 md:flex-row">
            <div className="shrink-0">
              <div className="relative w-full max-w-60 overflow-hidden rounded-lg border border-background-200 md:w-56">
                <img
                  src={manga.cover}
                  alt={`${manga.title}漫画封面`}
                  className="aspect-[5/7] w-full object-cover object-top"
                />
                <span
                  className={`absolute left-3 top-3 rounded-md px-2.5 py-1 text-xs font-medium text-background-50 ${
                    manga.status === '已完结' ? 'bg-accent-600' : 'bg-primary-600'
                  }`}
                >
                  {manga.status}
                </span>
              </div>
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                {manga.title}
              </h1>
              <p className="mt-1 text-sm text-foreground-600">作者：{manga.author}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {manga.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category/${getGenreIdByName(g)}`}
                    className="rounded-md bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg border border-background-200 bg-background-50 p-3 text-center">
                  <dt className="text-xs text-foreground-600">评分</dt>
                  <dd className="mt-1 flex items-center justify-center gap-1 text-lg font-black text-accent-600">
                    <i className="ri-star-fill text-sm"></i>{manga.score.toFixed(1)}
                  </dd>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3 text-center">
                  <dt className="text-xs text-foreground-600">总话数</dt>
                  <dd className="mt-1 text-lg font-black text-foreground-950">{manga.chapters}话</dd>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3 text-center">
                  <dt className="text-xs text-foreground-600">阅读热度</dt>
                  <dd className="mt-1 text-lg font-black text-primary-600">{manga.views}</dd>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3 text-center">
                  <dt className="text-xs text-foreground-600">更新频率</dt>
                  <dd className="mt-1 line-clamp-1 text-sm font-bold text-foreground-900">{manga.updateFrequency}</dd>
                </div>
              </dl>

              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="#"
                  onClick={(e) => e.preventDefault()}
                  className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-book-open-line mr-1"></i>
                  开始阅读
                </Link>
                <button
                  type="button"
                  onClick={() => setFollowing((v) => !v)}
                  className={`whitespace-nowrap rounded-full border px-6 py-3 text-sm font-semibold transition-colors ${
                    following
                      ? 'border-primary-500 bg-primary-100 text-primary-700'
                      : 'border-primary-300 text-primary-700 hover:bg-primary-100'
                  }`}
                >
                  <i className={`mr-1 ${following ? 'ri-check-line' : 'ri-add-line'}`}></i>
                  {following ? '已追更' : '追更'}
                </button>
                <button
                  type="button"
                  onClick={() => setCollected((v) => !v)}
                  className={`whitespace-nowrap rounded-full border px-6 py-3 text-sm font-semibold transition-colors ${
                    collected
                      ? 'border-accent-500 bg-accent-100 text-accent-800'
                      : 'border-accent-300 text-accent-700 hover:bg-accent-100'
                  }`}
                >
                  <i className={`mr-1 ${collected ? 'ri-heart-3-fill' : 'ri-heart-3-line'}`}></i>
                  {collected ? '已收藏' : '收藏'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <section>
              <h2 className="font-heading text-xl font-black text-foreground-950">作品简介</h2>
              <p className={`mt-3 text-sm leading-relaxed text-foreground-700 ${descOpen ? '' : 'line-clamp-3'}`}>
                {manga.description}
              </p>
              {!descOpen && (
                <button
                  type="button"
                  onClick={() => setDescOpen(true)}
                  className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  展开全部简介 <i className="ri-arrow-down-s-line"></i>
                </button>
              )}
            </section>

            <section className="mt-10">
              <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                <h2 className="font-heading text-xl font-black text-foreground-950">章节列表</h2>
                <button
                  type="button"
                  onClick={() => setAscOrder((v) => !v)}
                  className="whitespace-nowrap rounded-full border border-background-200 bg-background-100 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-secondary-100"
                >
                  <i className="ri-sort-desc mr-1"></i>
                  {ascOrder ? '从新到旧' : '从旧到新'}
                </button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {visibleChapters.map((c) => (
                  <Link
                    key={c.n}
                    to="#"
                    onClick={(e) => e.preventDefault()}
                    className="line-clamp-1 rounded-lg border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    {c.title}
                  </Link>
                ))}
              </div>
              {chapterCount < chapters.length && (
                <button
                  type="button"
                  onClick={() => setChapterCount((v) => v + 18)}
                  className="mt-5 w-full whitespace-nowrap rounded-lg border border-primary-300 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
                >
                  加载更多章节
                  <i className="ri-arrow-down-line ml-1"></i>
                </button>
              )}
            </section>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">作品信息</h2>
              <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                <li className="flex justify-between gap-3"><span className="text-foreground-500">作者</span><span>{manga.author}</span></li>
                <li className="flex justify-between gap-3"><span className="text-foreground-500">状态</span><span>{manga.status}</span></li>
                <li className="flex justify-between gap-3"><span className="text-foreground-500">最新</span><span className="line-clamp-1 text-right">{manga.latestChapter}</span></li>
                <li className="flex justify-between gap-3"><span className="text-foreground-500">更新时间</span><span>{manga.updateTime}</span></li>
                <li className="flex justify-between gap-3"><span className="text-foreground-500">类型</span><span>{manga.genres.join(' / ')}</span></li>
              </ul>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-xl font-black text-foreground-950">相关推荐</h2>
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {related.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}